import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, EyeOff } from "lucide-react";
import { useEditorStore } from "../../../../store/editorStore";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { SKU } from "../../../../types/sku";
import {
  spineStaticThumbUrl,
  spineStaticOriginalUrl,
} from "commons/cdn";

export interface StaticSortableSkuItemProps {
  sku: SKU;
  selectionId: string;
  isEditing: boolean;
  pro?: number;
  hidden?: boolean;
  onEdit: () => void;
}

export function StaticSortableSkuItem({
  sku,
  selectionId: _selectionId,
  isEditing,
  pro,
  hidden,
  onEdit,
}: StaticSortableSkuItemProps) {
  void _selectionId;
  const getSkuErrors = useEditorStore((s) => s.getSkuErrors);
  const skuErrors = getSkuErrors(sku.id);
  const hasError = skuErrors.length > 0;

  const thumbSrc = sku.thumbAssetId
    ? spineStaticThumbUrl(sku.thumbAssetId)
    : sku.originalAssetId
      ? spineStaticOriginalUrl(sku.originalAssetId)
      : "";

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sku.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative rounded-lg border p-1.5 transition-colors",
        hasError
          ? "border-amber-500 border-2 bg-amber-50/50"
          : isEditing
            ? "border-primary ring-2 ring-primary bg-primary/10"
            : "bg-muted/30 hover:bg-muted/50",
        isDragging && "opacity-50 shadow-lg z-10",
      )}
    >
      <div
        {...attributes}
        {...listeners}
        className="absolute top-0.5 left-0.5 h-5 w-5 flex items-center justify-center cursor-grab"
      >
        <GripVertical className="h-3 w-3 text-muted-foreground" />
      </div>
      <div className="cursor-pointer" onClick={onEdit} role="button">
        <div className="flex justify-center mb-1 mt-4">
          {thumbSrc ? (
            <img
              src={thumbSrc}
              alt=""
              className="h-14 w-14 object-contain rounded border bg-background"
            />
          ) : (
            <div className="h-14 w-14 rounded border border-dashed flex items-center justify-center text-[10px] text-muted-foreground text-center px-1">
              未上传
            </div>
          )}
        </div>
        <div className="text-center space-y-0.5">
          <span className="truncate block text-[10px] text-muted-foreground">
            静态图
          </span>
          <span className="truncate block text-[9px] font-mono text-muted-foreground/60">
            {sku.id}
          </span>
        </div>
        <div className="flex items-center justify-center gap-1 mt-0.5">
          <Badge
            variant={(pro || 0) > 0 ? "default" : "secondary"}
            className={cn(
              "text-[10px] px-1.5 h-4",
              (pro || 0) > 0 ? "bg-amber-500 hover:bg-amber-600" : "",
            )}
          >
            {(pro || 0) > 0 ? `¥${pro}` : "免费"}
          </Badge>
          {hidden && (
            <div
              className="h-4 w-4 rounded flex items-center justify-center bg-zinc-200"
              title="App 中隐藏"
            >
              <EyeOff className="h-2.5 w-2.5 text-zinc-600" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
